import { type CollectionSummaryJSONObject, IkeToken } from '@ikegps/core-api';
import { SystemTag } from '@ikegps/core-api';
import {
	CaptureProviderHelpers,
	CollectionModelField,
	CollectionModelFieldAnchor,
	CollectionModelFieldNumber,
	CollectionModelFieldSubFormInstance,
	CollectionModelFieldTrueSize,
	CollectionModelFieldVector,
	FormModelFieldSubForm,
	IkeAngle,
	IkeDraw,
	SVGRenderer,
	ZoomRenderer,
} from '@ikegps/core-shared';
import { CollectionModelDrawableLine, CollectionModelDrawableMarker, type SVGRenderOptions } from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { ModalService } from '../components/modal/modal.service';
import { addStyles, DefaultTheme } from '../components/theme';
import { T, Translate } from '../i18n';
import { m } from '../mithril';
import type { MithrilVNode } from '../mithril';
import { CollectionEdit } from '../modules/collection.view/collection.view';
import { CollectionEditCtrl } from '../modules/collection.view/collection.view.ctrl';
import { DrawablePropertiesComponent } from '../modules/collection.view/drawable.properties';
import { canUpdate } from '../modules/collection.view/fields/vertical.separation.field.view';
import { DrawToolbar } from '../modules/draw.toolbar/draw.toolbar';
import { HiddenImageIcon } from '../modules/hidden.image.icon/hidden.image.icon';
import { PhotomeasureCollectionList } from '../modules/photomeasure.collection.list/photomeasure.collection.list';
import { CSS as PhotomeasureCollectionListCSS } from '../modules/photomeasure.collection.list/photomeasure.collection.list.style';
import { Services } from '../services';
import { InputEventBindContext } from '../services/input/event.context';
import type { Modifiers } from '../services/input/input.service';
import { CurrentState } from '../state/page.state';
import { AppStore } from '../store';
import { BrowserCheck } from '../util/browser-check';
import { classNames } from '../util/classnames';
import { MissingPage } from './missing.page';
import { IkePage, type IkePageConfig, IkePageName } from './page';
import { PhotoMeasureInputEvents } from './photomeasure/pm.events';
import { TagState } from './tag.state';

const CSS = {
	container: 'c-PhotoMeasure',
	center: 'c-PhotoMeasure__center',
	right: 'c-PhotoMeasure__right',
	empty: 'c-PhotoMeasure__empty',
	hiddenCapture: 'c-PhotoMeasure__hiddenCapture',
	drawWrapper: 'c-PhotoMeasure__drawWrapper',
};

const Style = {
	[CSS.container]: {
		height: '100%',
		width: '100%',
		'min-width': '1000px',
		display: 'flex',
		'justify-content': 'space-between',
		[`& .${SVGRenderer.CSS.svg}`]: {
			outline: `1px solid ${DefaultTheme.font.color.divider}`,
		},
		[`& .${PhotomeasureCollectionListCSS.container}`]: {
			width: '25%',
			'min-width': '400px',
		},
	},
	[CSS.center]: {
		position: 'relative',
		display: 'flex',
		flex: 1,
	},
	[CSS.drawWrapper]: {
		position: 'relative',
		flex: 1,
		'text-align': 'center',
	},
	[CSS.right]: {
		position: 'relative',
		display: 'flex',
		width: '25%',
		'min-width': '420px',
	},
	[CSS.empty]: {
		display: 'flex',
		'align-items': 'center',
		'font-size': '250%',
		color: DefaultTheme.font.color.supporting,
	},
	[CSS.hiddenCapture + '>:not(i)']: {
		filter: 'grayscale(100%) brightness(40%)',
	},
};

addStyles(Style);
addStyles(ZoomRenderer.Style);
addStyles(SVGRenderer.Style);

export const PhotoMeasurePageConfig: IkePageConfig = {
	type: 'internal',
	name: IkePageName.collectionMeasure,
	path: '/measure',
	auth: {
		required: true,
	},
	layout: { nav: false },
};

interface QueryParams {
	/** Department Id */
	d: string;
	/** Tag Id */
	t: string;
	/** Collection Id */
	collection?: string;
	/** Capture Id */
	capture?: string;
}

const isVectorOrAnchor = (
	field: CollectionModelField
): field is CollectionModelFieldVector | CollectionModelFieldAnchor => {
	return CollectionModelFieldVector.isVectorField(field) || CollectionModelFieldAnchor.isAnchorField(field);
};

export const calculateOrientationBearing = (equipmentOrientation, vector): number | null | undefined => {
	const floatValue = equipmentOrientation?.displayValue ? parseFloat(equipmentOrientation?.displayValue) : null;
	return floatValue || floatValue === 0
		? IkeAngle.toRadians(floatValue)
		: vector?.value && typeof vector.value === 'object' && 'bearing' in vector.value
			? vector.value.bearing
			: null;
};

const recursiveSearchParentForVector = (
	subFormInstance: CollectionModelFieldSubFormInstance
): CollectionModelFieldVector | CollectionModelFieldAnchor => {
	let result: CollectionModelFieldVector | CollectionModelFieldAnchor = null;

	subFormInstance.forEachField((field) => {
		if (isVectorOrAnchor(field)) {
			result = field;
		}
	});

	if (!result && subFormInstance.parent.parent instanceof CollectionModelFieldSubFormInstance) {
		result = recursiveSearchParentForVector(subFormInstance.parent.parent);
	}

	return result;
};

const recursiveSearchParentForOrientation = (
	subFormInstance: CollectionModelFieldSubFormInstance
): CollectionModelFieldNumber => {
	let result: CollectionModelFieldNumber = null;

	subFormInstance.forEachField((field) => {
		if (
			field instanceof CollectionModelFieldNumber &&
			(field.schema.fieldId === 'ipf_equipment:ipf_equipmentOrientation' ||
				field.schema.fieldId === 'equipment:equipmentOrientation')
		) {
			result = field;
		}
	});

	if (!result && subFormInstance.parent.parent instanceof CollectionModelFieldSubFormInstance) {
		result = recursiveSearchParentForOrientation(subFormInstance.parent.parent);
	}

	return result;
};

export class PhotoMeasurePage extends IkePage {
	tagState: TagState;

	events: PhotoMeasureInputEvents;
	editControl: CollectionEditCtrl;
	list: PhotomeasureCollectionList;
	detail: CollectionEdit;
	eventContext: InputEventBindContext;
	browserCheck: BrowserCheck;

	@observable invalidUrl: boolean = false;
	@observable inspectedVector: CollectionModelFieldAnchor | CollectionModelFieldVector = null;
	@observable inspectedNumber: CollectionModelFieldNumber = null;
	@observable hoveredVector: CollectionModelFieldAnchor | CollectionModelFieldVector = null;
	@observable hoveredNumber: CollectionModelFieldNumber = null;

	constructor() {
		super(PhotoMeasurePageConfig);
		makeObservable(this);

		this.browserCheck = new BrowserCheck();
	}

	init() {
		CurrentState.reset();

		this.tagState = new TagState();

		this.events = new PhotoMeasureInputEvents(this, this.draw);
		this.events.init();
		this.list = new PhotomeasureCollectionList(this.tagState);
		this.editControl = new CollectionEditCtrl(this.tagState.map);
		this.editControl.isPhotomeasure = true;

		this.detail = new CollectionEdit(this.editControl);

		this.eventContext = Services.Input.getContext();

		this.reaction(
			() => CurrentState.inspectedCollectionRevision,
			() => {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				this.updateInspectedCapture();
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				this.updateURL();
			}
		);

		this.reaction(
			() => {
				const draw = this.draw;
				if (draw == null) {
					return null;
				}
				return draw.state.propertiesId;
			},
			(drawableId: string) => this.viewProperties(drawableId)
		);

		this.reaction(
			() => CurrentState.hoverId,
			(hoverId) => {
				if (hoverId && CurrentState.inspectedCollectionRevision) {
					const collection = AppStore.Collection.get(CurrentState.inspectedCollectionRevision);

					let field = collection?.findField(hoverId);

					if (!field) return;

					if (
						field instanceof CollectionModelFieldNumber &&
						(field.schema.fieldId === 'ipf_equipment:ipf_equipmentOrientation' ||
							field.schema.fieldId === 'equipment:equipmentOrientation')
					) {
						this.setHoveredNumber(field);

						return;
					} else if (field.parent instanceof CollectionModelFieldSubFormInstance) {
						const result = recursiveSearchParentForOrientation(field.parent);
						if (result) {
							this.setHoveredNumber(result);
							return;
						}
					}

					if (isVectorOrAnchor(field)) {
						this.setHoveredVector(field);

						return;
					} else if (field.parent instanceof CollectionModelFieldSubFormInstance) {
						const result = recursiveSearchParentForVector(field.parent);

						if (result) {
							this.setHoveredVector(result);
							return;
						}
					}
				}
				this.setHoveredVector(null);
				this.setHoveredNumber(null);
			}
		);

		this.reaction(
			() => CurrentState.inspectedFieldId,
			(inspectedFieldId) => {
				if (inspectedFieldId && CurrentState.inspectedCollectionRevision) {
					const collection = AppStore.Collection.get(CurrentState.inspectedCollectionRevision);

					let field: CollectionModelField = collection.findField(inspectedFieldId);

					if (!field) {
						this.setInspectedNumber(null);
						this.setInspectedVector(null);
						return;
					}

					if (
						field instanceof CollectionModelFieldNumber &&
						(field.schema.fieldId === 'ipf_equipment:ipf_equipmentOrientation' ||
							field.schema.fieldId === 'equipment:equipmentOrientation')
					) {
						this.setInspectedNumber(field);

						return;
					} else if (field.parent instanceof CollectionModelFieldSubFormInstance) {
						const result = recursiveSearchParentForOrientation(field.parent);
						if (result) {
							this.setInspectedNumber(result);
							return;
						}
					}
					// @todo when anchor location types exist, we need to use the geolib
					// to calculate the bearing, as the anchor may not have a vector provider
					// it might be worth removing the isVectorOrAnchor helper when we integrate this.
					if (isVectorOrAnchor(field)) {
						this.setInspectedVector(field);

						return;
					} else if (field.parent instanceof CollectionModelFieldSubFormInstance) {
						const result = recursiveSearchParentForVector(field.parent);
						if (result) {
							this.setInspectedVector(result);
							return;
						}
					}
				}

				this.setInspectedNumber(null);
				this.setInspectedVector(null);
			}
		);

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.updateInspectedCapture();
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.parseURL(Object.fromEntries(Services.Route.params) as any);

		if (
			Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE) ||
			Services.Token.has(IkeToken.FEATURE_CTRL_SHIFT_SELECT)
		) {
			if (this.browserCheck.os === 'Mac OS') {
				// macos
				this.registerCtrlShiftPressed('keydown', true, { meta: true, shift: true });
				this.registerCtrlShiftPressed('keyup', false, { meta: true });
			}

			// windows
			this.registerCtrlShiftPressed('keydown', true, { ctrl: true, shift: true });
			this.registerCtrlShiftPressed('keyup', false, { ctrl: true });

			// common
			this.registerCtrlShiftPressed('keyup', false, { shift: true });
		}

		this.reaction(
			() => this.draw,
			() => {
				if (this.draw) {
					this.draw.state.setAutoArrangeAnnotations(this.draw.drawState.hasOnlyInsightDetections());
				}
			}
		);
	}

	get nodes(): MithrilVNode | MithrilVNode[] {
		if (this.invalidUrl) {
			return new MissingPage().view();
		}

		if (Services.Department.current == null) {
			return null;
		}

		if (this.tagState == null) {
			this.init();
		}

		return m('div', { className: CSS.container }, [
			this.list.view(),
			this.viewCenter(),
			m('div', { className: CSS.right }, [this.detail.view()]),
		]);
	}

	override onRemove() {
		super.onRemove();
		this.tagState.destroy();
		this.events.destroy();

		if (this.editControl) {
			this.editControl.destroy();
		}
	}

	private viewCenter(): MithrilVNode {
		if (this.draw != null) {
			let vector: CollectionModelFieldAnchor | CollectionModelFieldVector | null = null;
			let equipmentOrientation: CollectionModelFieldNumber | null = null;
			const hidden = this.draw.state.imageVisibilityHidden;
			if (this.hoveredNumber || this.hoveredVector) {
				vector = this.hoveredVector || null;
				equipmentOrientation = this.hoveredNumber || null;
			} else if (this.inspectedNumber || this.inspectedVector) {
				vector = this.inspectedVector || null;
				equipmentOrientation = this.inspectedNumber || null;
			}

			const indicatorColor =
				!equipmentOrientation && vector && vector.parent && Services.Token.has(IkeToken.PHOTOMEASURE_COLOR_CODING)
					? FormModelFieldSubForm.getSpanColor(vector.parent)
					: 'white';

			// I hate that we have to do this, but there is no other way to figure out where a capture is coming from to be able to determine if it is markable or not
			this.draw.collection.forEachField((field) => {
				if (
					CollectionModelFieldTrueSize.isTrueSizeField(field) &&
					field.getCaptureIds().includes(this.draw.capture.objectId)
				) {
					this.draw.isMarkable = true;
				}
			});

			const isIkeAnalyst = Services.User.isIkeAnalyst(Services.User.current);

			const renderOptions: SVGRenderOptions = {
				draw: this.draw,
				units: Services.UserSettings.units,
				isEditable: this.draw.isMarkable, // TODO check the permission of the user on the collection
				isInteractive: true,
				isThumbnail: false,
				isInsightMarkerLabelsVisible: Services.Token.has(IkeToken.FEATURE_INSIGHT_MARKER_LABELS),
				isInsightMarkersVisible:
					(Services.Token.has(IkeToken.FEATURE_INSIGHT_AHOA) || isIkeAnalyst) && this.draw.state.visibleInsightMarkers,
				insightMarkerTypesVisible: Services.Token.getByPrefix('feature.insight.marker.').map(
					(token) => token.split('.').pop()!
				),
				isInsightMaskVisible:
					(Services.Token.has(IkeToken.FEATURE_INSIGHT_AHOA) || isIkeAnalyst) && this.draw.state.visibleInsightMasks,
				insightMaskTypesVisible:
					Services.Token.has(IkeToken.FEATURE_INSIGHT_AHOA) || isIkeAnalyst
						? Services.Token.getByPrefix('feature.insight.mask.').map((token) => token.split('.').pop()!)
						: [],
				isVerticalSeparationEnabled: canUpdate(),
				useColorCoding: this.editControl.useColorCoding,
				showOrientation: Services.Token.has(IkeToken.FEATURE_PHOTOMEASURE_COMPASS) || isIkeAnalyst,
				orientationBearing: calculateOrientationBearing(equipmentOrientation, vector),
				indicatorColor,
				enableRoll: Services.Token.has(IkeToken.FEATURE_PHOTOMEASURE_ROLL) && this.draw.isMarkable,
				scale: SVGRenderer.scaleUI(this.draw.state, false),
			};

			// Update SVG render options in PhotoMeasureInputEvents so that the Arrange Annotations hotkey has access to it
			this.events.renderOptions = renderOptions;

			// Update the IkeDraw in PhotoMeasureInputEvents because the selected photo may have changed
			this.events.draw = this.draw;

			return m('div', { className: CSS.center }, [
				m('div', { className: classNames(CSS.drawWrapper, { [CSS.hiddenCapture]: hidden }) }, [
					SVGRenderer.view(renderOptions),
					hidden && HiddenImageIcon.view(),
				]),
				// We put the tool bar last as it needs to render over the top of everything
				DrawToolbar.view(this.draw, this.detail.zoomRenderer.isShown, renderOptions),
			]);
		}

		if (CurrentState.inspectedCollectionRevision != null) {
			const collectionSummary = AppStore.CollectionSummary.get(CurrentState.inspectedCollectionRevision);

			if (collectionSummary != null) {
				return m(
					'div',
					{ className: CSS.empty },
					CaptureProviderHelpers.getCaptureIds(this.editControl.collection).length
						? Translate(T.SELECT_CAPTURE)
						: Translate(T.NO_CAPTURES)
				);
			}
		}

		return m('div', { className: SVGRenderer.CSS.container });
	}

	private async parseURL(params: QueryParams) {
		const activeRoute = Services.Route.active;

		if (activeRoute == null || activeRoute.config.name !== this.config.name) {
			return;
		}

		const departmentId = params.d;
		const tagId = params.t;
		const collectionUUID = params.collection;
		const captureId = params.capture;

		if (!Services.Department.hasDepartment(departmentId)) {
			this.setInvalidURL(true);
			Services.Log.Logger.warn({ departmentId }, 'Department Id in URL is invalid');
			return;
		}
		Services.Department.setDepartment(departmentId);

		const tag = await AppStore.Tag.fetch(tagId);
		if (tag == null) {
			this.setInvalidURL(true);
			Services.Log.Logger.warn({ tagId }, 'Tag Id in URL is invalid');
			return;
		}
		Services.Search.filterState.addTagFilter(tag);

		let collectionSummary: CollectionSummaryJSONObject;
		if (collectionUUID != null) {
			collectionSummary = await AppStore.CollectionSummary.getFetchableByUuid(collectionUUID).refetch();
		} else {
			const tagSummary = await AppStore.TagSummary.getFetchable(tagId).fetch();
			collectionSummary = await AppStore.CollectionSummary.fetch(tagSummary.revisions[0]);
		}

		if (collectionSummary == null) {
			this.setInvalidURL(true);
			Services.Log.Logger.warn({ collectionUUID }, 'Collection Id in URL is invalid');
			return;
		}

		if (collectionSummary.tags.indexOf(SystemTag.deleted) === -1) {
			CurrentState.setInspectedCollection(collectionSummary.revision, captureId);
		} else {
			CurrentState.setInspectedTagId(tagId);
		}
	}

	/**
	 * @private
	 * @deprecated This property will be set to private. Any outside access should go through new public methods that encapsulate the draw state.
	 */
	get draw(): IkeDraw {
		const editState = CurrentState.CollectionEdit;
		if (
			!editState.isEditing ||
			CurrentState.CollectionEdit.collectionRevision !== CurrentState.inspectedCollectionRevision
		) {
			return null;
		}

		if (CurrentState.inspectedCaptureId == null) {
			return null;
		}

		return editState.getIkeDraw(CurrentState.inspectedCaptureId);
	}

	async viewProperties(drawableId: string) {
		if (drawableId == null || this.draw == null) {
			return;
		}
		if (ModalService.isOpen) {
			return;
		}

		const drawState = this.draw.state;

		const drawable = this.draw.drawState.getDrawable<CollectionModelDrawableLine | CollectionModelDrawableMarker>(
			drawableId
		);

		const inputModal = new DrawablePropertiesComponent(drawable);
		if (!inputModal.isValid) {
			return drawState.propertiesHide();
		}
		const response = await inputModal.open();
		if (!response.ok) {
			return drawState.propertiesHide();
		}

		if (CollectionModelDrawableMarker.isBase(drawable)) {
			this.setBaseOffset(drawable, inputModal.offsetState.value);
		} else {
			if (inputModal.state.value == null || inputModal.state.value.toString().trim().length === 0) {
				this.setMarkerLabel(drawable, null);
			} else {
				this.setMarkerLabel(drawable, inputModal.state.value as string);
			}
		}

		// Approve the insight flag from the drawable if the user adds a manual label
		if (drawable.isApprovableInsightDrawable()) {
			drawable.approveInsightFlag();
		}

		drawState.propertiesHide();
	}

	@action private setBaseOffset(drawable: CollectionModelDrawableMarker, value: number) {
		drawable.offset = value;
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Collection,
			'tool:base:offset',
			this.draw.collection.revision
		);
	}

	@action private setMarkerLabel(drawable: CollectionModelDrawableLine, value: string) {
		value = value != null ? value.trim() : '';

		if (value.length > 0) {
			drawable.label.text = [value];
		} else {
			drawable.label.text = [];
		}

		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Collection,
			'tool:label',
			this.draw.collection.revision
		);
	}

	private async updateInspectedCapture() {
		if (CurrentState.inspectedCollectionRevision == null) {
			return;
		}

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.tagState.map.zoomToCollection(CurrentState.inspectedCollectionRevision);

		const collection = await AppStore.Collection.fetch(CurrentState.inspectedCollectionRevision);
		if (collection.revision !== CurrentState.inspectedCollectionRevision) {
			// Inspected collection may change while downloading collection
			return;
		}

		let captureId = CurrentState.inspectedCaptureId;
		if (captureId == null) {
			captureId = Services.Collection.getFirstCaptureId(collection);
		}

		this.events.trackEvents();
		CurrentState.setInspectedCollection(CurrentState.inspectedCollectionRevision, captureId);
	}

	private async updateURL() {
		const departmentId = Services.Department.current.objectId;
		const tagId: string = CurrentState.inspectedTagId || Services.Route.params.get('t');

		if (departmentId == null || tagId == null || CurrentState.inspectedCollectionRevision == null) {
			return;
		}

		const collectionSummary = await AppStore.CollectionSummary.fetch(CurrentState.inspectedCollectionRevision);
		const collectionUUID = collectionSummary.uuid;
		const captureId = CurrentState.inspectedCaptureId;

		// Don't update history if current URL only contains departmentId and tagId
		const updateHistory = Services.Route.params.get('capture') != null;

		Services.Route.setAllParams(
			{
				d: departmentId,
				t: tagId,
				collection: collectionUUID,
				capture: captureId,
			},
			updateHistory
		);
	}

	@action private setInvalidURL(invalid: boolean) {
		this.invalidUrl = invalid;
	}

	@action private setInspectedVector(field: CollectionModelFieldVector | CollectionModelFieldAnchor) {
		this.inspectedVector = field;
	}

	@action private setInspectedNumber(field: CollectionModelFieldNumber) {
		this.inspectedNumber = field;
	}

	@action private setHoveredVector(field: CollectionModelFieldVector | CollectionModelFieldAnchor) {
		this.hoveredVector = field;
	}

	@action private setHoveredNumber(field: CollectionModelFieldNumber) {
		this.hoveredNumber = field;
	}

	override onBeforeUnload() {
		this.list.overlay.capture = null;
		CurrentState.inspectedCaptureId = null;
	}

	private registerCtrlShiftPressed(eventType: 'keydown' | 'keyup', isPressed: boolean, modifiers: Partial<Modifiers>) {
		this.eventContext.on(
			this.eventContext.Code.AllKeys,
			() => {
				CurrentState.setIsCtrlShiftPressed(isPressed);
			},
			{
				eventType,
				modifiers,
			}
		);
	}
}
